import React, { useState } from 'react';
import { BuilderComponent } from '@builder.io/react';
import Link from 'next/link';
import cn from 'classnames';
import { useSpring, animated, config } from 'react-spring';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import Loading from 'react-scale/components/elements/loading';

import ScaleLogo from 'react-scale/components/tailwind/ScaleLogo';
import useWindowSize from 'react-scale/components/tailwind/useWindowSize';
import VideoOverlay from 'react-scale/components/elements/video-overlay';
import { fetchAnnouncementBanner } from 'react-scale/lib/announcement-banner-fetch';

import { themeLight, themeDark } from './header-themes';
import NavItem from './NavItem';
import AnimatedNavbar from './AnimatedNavbar';

import NavMobile from './NavMobile';

import 'react-scale/components/builder-banners';

const getResolvedTheme = theme => {
  let resolvedTheme;

  if (typeof theme === 'string') {
    resolvedTheme = theme === 'dark' ? themeDark : themeLight;
  } else {
    resolvedTheme = theme;
  }

  return resolvedTheme;
};

const Header = ({
  theme,
  background,
  border,
  borderColor,
  videoOverlay,
  demoMode,
  signupURL,
  noIndex,
  prefetchedData = { eventsBanner: null, announcementBanner: null },
  announcementBannerContainerProps = {},
  ...props
}) => {
  const hasPrefetchedData = Boolean(
    prefetchedData?.eventsBanner || prefetchedData?.announcementBanner
  );
  const [navMobile, setNavMobile] = useState(false);
  const [eventsBanner, setEventsBanner] = useState(prefetchedData?.eventsBanner);
  const [announcementBanner, setAnnouncementBanner] = useState(prefetchedData?.announcementBanner);
  const [bannersLoading, setBannersLoading] = useState(hasPrefetchedData === false);

  const router = useRouter();

  const handleClick = () => {
    setNavMobile(!navMobile);
  };

  const { width } = useWindowSize();

  React.useEffect(() => {
    const fetchBuilderData = async () => {
      const announcementBannerData = await fetchAnnouncementBanner(router.asPath);

      setEventsBanner(announcementBannerData.eventsBanner);
      setAnnouncementBanner(announcementBannerData.announcementBanner);

      setTimeout(() => {
        setBannersLoading(false);
      }, 300);
    };

    if (!hasPrefetchedData) {
      // fetchthe data only if its not pre-fetched
      fetchBuilderData();
    }
  }, [hasPrefetchedData, router.asPath]);

  const mobileView = width < 1024;

  const resolvedTheme = getResolvedTheme(theme);

  const slideInProp = useSpring({
    opacity: navMobile ? 1 : 0,
    transform: navMobile ? 'scale(1)' : 'scale(0.95)',
    config: config.stiff,
    from: { opacity: 0, transform: 'scale(0.95)' }
  });

  const signUpLink = () => {
    switch (router.asPath) {
      case '/nucleus':
        return 'https://dashboard.scale.com/login?redirect_url=nucleus';
      case '/studio':
        return 'https://dashboard.scale.com/signup?product=studio';
      case '/lex':
        return 'https://dashboard.scale.com/signup?product=rapid&utm_medium=lex-podcast&utm_source=podcast&utm_campaign=acquisition-rapid-lex-podcast&utm_content=lex&referral_code=lex';
      default:
        return 'https://dashboard.scale.com/login';
    }
  };

  if (demoMode) {
    return (
      <header
        className={cn(background || resolvedTheme.mainBackground, 'text-left relative z-50', {
          [`${borderColor} border-b`]: border
        })}
      >
        <Loading />
        <div className="container">
          <div className="flex items-center h-20 lg:h-24">
            <HeaderLogo color={resolvedTheme.logo} />
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {!props.builderBlock && (
        <NextSeo
          noindex={noIndex}
          openGraph={
            !!props.ogImg && {
              images: [
                {
                  url: props.ogImg,
                  width: 1200,
                  height: 630,
                  alt: 'Og Image Alt',
                  type: 'image/jpeg'
                }
              ]
            }
          }
          {...props}
        />
      )}
      <header
        className={cn(background || resolvedTheme.mainBackground, 'text-left relative z-50', {
          [`${borderColor} border-b`]: border
        })}
      >
        <Loading />
        <div>
          {/* {bannersLoading && (
            <div className="text-white transition-all bg-black h-[49px]">
              <div className="container flex items-center h-full">
                <div className="space-y-2.5 animate-pulse w-full">
                  <div className="flex items-center w-full space-x-2">
                    <div className="h-2.5 bg-neutral-700 rounded-full  w-32" />
                    <div className="h-2.5 bg-neutral-800 rounded-full  w-24" />
                    <div className="h-2.5 bg-neutral-800 rounded-full  w-full" />
                  </div>
                </div>
              </div>
            </div>
          )} */}
          {!bannersLoading && (
            <div {...announcementBannerContainerProps}>
              <BuilderComponent model="announcement-banner" content={announcementBanner} />
              <BuilderComponent model="events-banner" content={eventsBanner} />
            </div>
          )}
        </div>

        {videoOverlay && <VideoOverlay />}
        <div className="container">
          <div className="flex items-center h-20 lg:h-24 lg:relative lg:justify-between">
            <div className="flex justify-between w-full lg:w-auto lg:relative lg:z-10">
              <div className="flex items-center">
                <HeaderLogo color={resolvedTheme.logo} />
              </div>
              <MenuMobile onClick={handleClick} color={resolvedTheme.logo} />
            </div>
            <animated.div
              className={cn(
                'opacity-0 lg:opacity-100 absolute lg:static',
                navMobile && mobileView
                  ? 'pointer-events-auto'
                  : 'pointer-events-none lg:pointer-events-auto',
                {
                  'inset-0 m-3 z-40 origin-top-right transform scale-100': mobileView
                }
              )}
              style={navMobile ? slideInProp : null}
            >
              <div
                className={cn({
                  [`${resolvedTheme.background} shadow-lg rounded-lg overflow-hidden`]: mobileView
                })}
              >
                {mobileView && (
                  <div
                    className={cn({
                      [` right-4 z-10 top-4 flex items-center justify-center w-6 h-6 ${resolvedTheme.closeButton.background} rounded-full cursor-pointer absolute`]:
                        mobileView
                    })}
                    onClick={handleClick}
                  >
                    <svg width="8" height="8" viewBox="0 0 12 12">
                      <rect
                        width="15.3125"
                        height="1.48871"
                        transform="matrix(0.701055 -0.713108 0.701055 0.713108 0 10.9385)"
                        fill={resolvedTheme.closeButton.cross}
                      />
                      <rect
                        width="15.3125"
                        height="1.48871"
                        transform="matrix(0.701055 0.713108 -0.701055 0.713108 1.26514 0)"
                        fill={resolvedTheme.closeButton.cross}
                      />
                    </svg>
                  </div>
                )}
                <div className="z-auto hidden lg:-translate-x-12 xl:translate-x-0 lg:absolute lg:left-0 lg:right-0 lg:justify-center lg:items-center lg:flex">
                  <AnimatedNavbar theme={resolvedTheme} duration={300} />
                </div>
                <div className="z-auto p-4 lg:hidden">
                  <NavMobile theme={resolvedTheme} navResponsive={mobileView} />
                </div>
                <nav
                  className={`${resolvedTheme.dropdown.backgroundBottomMobile} lg:bg-transparent text-center lg:text-left list-none lg:flex py-2 lg:py-0`}
                >
                  <ul className="grid items-center grid-cols-2 py-0 text-center list-none bg-transparent justify-items-center lg:flex lg:text-left">
                    <NavItem title="Book a Demo" href="/demo" color={resolvedTheme.logo} />
                    <NavItem
                      title="Log In"
                      href={signupURL || signUpLink()}
                      color={resolvedTheme.logo}
                    />
                  </ul>
                </nav>
              </div>
            </animated.div>
          </div>
        </div>
      </header>
    </>
  );
};

const HeaderLogo = ({ color }) => (
  <div className="h-5 -mt-2">
    <Link href="/">
      <ScaleLogo className={cn('w-auto h-full fill-current', color)} />
    </Link>
  </div>
);

const MenuMobile = ({ onClick, color }) => (
  <div className="flex items-center cursor-pointer lg:hidden" onClick={onClick}>
    <svg className={cn('w-6 h-auto fill-current', color)} viewBox="0 0 24 12">
      <rect width="24" height="2" />
      <rect y="5" width="24" height="2" />
      <rect y="10" width="24" height="2" />
    </svg>
  </div>
);

export const HeaderSpacing = () => <div className="pt-10 lg:pt-12" />;

Header.defaultProps = {
  demoMode: false,
  theme: 'dark', // for header theme
  background: 'bg-transparent',
  border: false,
  borderColor: 'border-neutral-200',
  videoOverlay: false,
  noIndex: false
};

export default Header;
